import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import TopNav from "../components/TopNav.jsx";

export default function SeedPage() {
  const [content, setContent] = useState("");
  const [meta, setMeta] = useState({ chars: 0, updated: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [versions, setVersions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [previewing, setPreviewing] = useState(null); // {version_id, content}

  const loadVersions = useCallback(async () => {
    try {
      const d = await api.seedVersions(30);
      setVersions(d.versions || []);
    } catch {}
  }, []);

  useEffect(() => {
    api.seed.get().then((s) => {
      setContent(s.content || "");
      setMeta({ chars: s.chars, updated: s.updated });
      setLoading(false);
    }).catch((e) => { toast.error(e.message); setLoading(false); });
    loadVersions();
  }, [loadVersions]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.seed.put(content);
      setMeta({ chars: r.chars, updated: r.updated });
      toast.success("Seed saved. Sage reads this on every new session.");
      loadVersions();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  useEffect(() => {
    if (loading) return;
    const t = setTimeout(async () => {
      try {
        const r = await api.seed.put(content);
        setMeta({ chars: r.chars, updated: r.updated });
        loadVersions();
      } catch {}
    }, 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [content]);

  const previewVersion = async (v) => {
    try {
      const doc = await api.seedVersion(v.version_id);
      setPreviewing({ version_id: v.version_id, content: doc.content || "", at: v.at });
    } catch (e) { toast.error(e.message); }
  };

  const rollback = async () => {
    if (!previewing) return;
    if (!window.confirm("Restore this version? Your current Seed will be snapshotted first, so you can roll forward again if needed.")) return;
    try {
      const r = await api.seedRollback(previewing.version_id);
      const s = await api.seed.get();
      setContent(s.content || "");
      setMeta({ chars: s.chars, updated: s.updated });
      setPreviewing(null);
      loadVersions();
      toast.success(`Restored version · ${r.chars.toLocaleString()} chars`);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain relative">
      <TopNav />
      <div className="mx-auto max-w-4xl px-4 md:px-8 py-8">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">Your Seed · owned by you</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tighter uppercase text-sage-text mb-4">
          What Sage remembers about you.
        </h1>
        <p className="text-sm text-sage-muted leading-relaxed max-w-2xl mb-6 sage-prose">
          Everything Sage knows about you between sessions lives here — in plain text you own and edit. Nothing gets added without you typing it. Sage reads this fresh on every new session, then forgets what wasn't saved.
        </p>

        <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-2 flex justify-between items-center">
          <span>Editable · Markdown supported</span>
          <div className="flex items-center gap-4">
            <button
              data-testid="seed-history-toggle"
              onClick={() => setShowHistory((v) => !v)}
              className="hover:text-sage-accent transition-colors duration-150"
              title="Every save creates a version. Roll back any time."
            >
              {showHistory ? "hide history" : `history · ${versions.length}`}
            </button>
            <span data-testid="seed-meta">
              {meta.chars.toLocaleString()} chars · {meta.updated ? `saved ${new Date(meta.updated).toLocaleString()}` : "never saved"}
            </span>
          </div>
        </div>

        <textarea
          value={previewing ? previewing.content : content}
          onChange={(e) => { if (!previewing) setContent(e.target.value); }}
          disabled={loading || !!previewing}
          placeholder={loading ? "loading…" : `# Who I am\n\n\n# What I'm building\n\n\n# How I like to be talked to\n\n\n# Things Sage should know about me\n`}
          data-testid="seed-textarea"
          className="w-full h-[520px] bg-sage-elevated border border-sage-border p-4 text-sage-text placeholder:text-sage-faint focus:border-sage-accent transition-colors duration-150 resize-none font-mono text-sm sage-prose"
        />

        {previewing && (
          <div className="mt-3 border border-sage-accent bg-sage-elevated p-3 text-xs flex justify-between items-center" data-testid="seed-preview-banner">
            <span className="text-sage-accent">
              Previewing version from {new Date(previewing.at).toLocaleString()} · {previewing.content.length.toLocaleString()} chars — read-only.
            </span>
            <div className="flex gap-3">
              <button
                data-testid="seed-preview-cancel"
                onClick={() => setPreviewing(null)}
                className="border border-sage-border text-sage-muted px-3 py-1 uppercase tracking-[0.1em] hover:text-sage-text"
              >
                Cancel
              </button>
              <button
                data-testid="seed-preview-rollback"
                onClick={rollback}
                className="border border-sage-accent text-sage-accent px-3 py-1 uppercase tracking-[0.1em] hover:bg-sage-accent hover:text-sage-bg"
              >
                Restore this version
              </button>
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-3 items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint max-w-md">
            Cap: 200,000 characters. Your Seed adds to input tokens on every reply — larger Seed = higher per-message cost, tracked live on the meter.
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => { if (window.confirm("Clear your Seed?")) { setContent(""); } }}
              disabled={!!previewing}
              data-testid="seed-clear"
              className="border border-sage-border text-sage-muted px-4 py-2 uppercase tracking-[0.1em] text-xs hover:border-sage-err hover:text-sage-err transition-colors duration-150 disabled:opacity-40"
            >
              Clear
            </button>
            <button
              onClick={save}
              disabled={saving || loading || !!previewing}
              data-testid="seed-save"
              className="border border-sage-accent text-sage-accent px-6 py-2 uppercase tracking-[0.1em] text-xs hover:bg-sage-accent hover:text-sage-bg transition-colors duration-150 disabled:opacity-50"
            >
              {saving ? "…" : "Save Seed"}
            </button>
          </div>
        </div>

        {showHistory && (
          <div className="mt-8 border border-sage-border" data-testid="seed-history-panel">
            <div className="p-3 border-b border-sage-border text-[10px] uppercase tracking-[0.15em] text-sage-muted flex justify-between">
              <span>Seed History · every save, every rollback</span>
              <span className="text-sage-faint tabular-nums">{versions.length}</span>
            </div>
            {versions.length === 0 ? (
              <div className="p-4 text-xs text-sage-faint">No prior versions yet.</div>
            ) : (
              <div className="divide-y divide-sage-border">
                {versions.map((v) => (
                  <div key={v.version_id} className="p-3 text-xs flex items-center justify-between gap-4" data-testid={`seed-version-${v.version_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sage-text tabular-nums">{new Date(v.at).toLocaleString()}</div>
                      <div className="text-sage-faint tabular-nums">
                        {v.chars.toLocaleString()} chars → replaced by {(v.replaced_by_chars ?? 0).toLocaleString()} chars
                        {v.reason ? ` · ${v.reason}` : ""}
                      </div>
                      <div className="text-sage-faint font-mono truncate">sha256 · {v.sha256}</div>
                    </div>
                    <button
                      data-testid={`seed-version-preview-${v.version_id}`}
                      onClick={() => previewVersion(v)}
                      className="border border-sage-border text-sage-muted px-3 py-1 uppercase tracking-[0.1em] hover:border-sage-accent hover:text-sage-accent"
                    >
                      Preview
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
